import { Injectable } from '@angular/core';
import { TrimConfiguration } from './models/trim-config.model';
import { StoryModel } from '../story/models/story.model';
import { Store } from '@ngxs/store';
import { SerializeChapterService } from './serialize/chapter/serialize-chapter.service';
import { serializeGraph } from './serialize/scene-graph/serialize-graph';
import { cloneDeep, invert } from 'lodash';
import { ChapterModel } from '../story/models/chapter.model';
import { EngineService } from '../engine/engine.service';
import { BdGraphPropertyValueMap, GraphNodeState } from 'src/lib/graph/graph.model';
import { StoryAnimationModel } from '../story/models/story.model';
import { AnimationService } from '../engine/animation.service';
import { renamespace } from './serialize/renamespace/renamespace';
import { StoryLabelsState } from '../story/state/labels/story.labels.state';
import { EngineParticlesState } from '../engine/state/particles/particles.state';
import { QuizState } from '../quiz/state/quiz.state';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { ChapterService } from '../story/chapter.service';
import { EngineNodesState } from '../engine/state/nodes/engine.nodes.state';

declare const Human;

@Injectable({
  providedIn: 'root'
})
export class SerializeModuleService {

  constructor(
    private store: Store,
    private engineService: EngineService,
    private chapterSerializer: SerializeChapterService,
    private animationService: AnimationService,
    private chapterService: ChapterService
  ) {}

  private serializeUI(story: StoryModel) {

    const ui = { ...story.ui };
    if (story.chapters.length > 1) {
      ui.scrubber = false;
      ui.playPause = false;
      ui.tour = { chapters: true };
    }
    return ui;

  }

  // #NOTE: [EM] This shouldn't be necessary. The user should be able to explicitly state their intention.
  // programatically create the animation property based on the currently active chapter's timeline data
  private serializeAnimation(zeroethChapter: ChapterModel) {
    // is there data in the current chapter's timeline map?
    const zeroethChapterAnimated = this.animationService.isChapterAnimated(zeroethChapter);
  
    if (zeroethChapterAnimated) {
      return {
        startTime: zeroethChapter.timelineMap[0].start,
        loop: zeroethChapter.loop !== false, // default to true
        numChapters: 1
      };
    }
    
    return {};
  }

  private getClippedObjects(): Record<string, any> {
    const activeClips = Human.renderer.clip.getActiveClips();

    if (activeClips.length === 0) {
      return {};
    }

    return Human.scene.getLeaves().reduce((acc, objectId) => {
      const object = Human.scene.objects[objectId];
      if (object.culledByClip) {
        acc[objectId] = true;
      }
      return acc;
    }, {});
  }

  // creates a map of objects to trim from the scene graph
  private generateTrimObjectMap(trimConfig: TrimConfiguration): BdGraphPropertyValueMap {
    
    const trimObjectState = {};

    if (trimConfig.graph.hidden) {
      
      const allNodeState = this.store.selectSnapshot(EngineNodesState.getAllNodeState);
      
      Object.values(allNodeState).forEach((chapterNodeState) => {

        Object.entries(chapterNodeState).forEach(([nodeId, nodeState]) => {
          // Include objects whose shown state was set by a tween, regardless
          // of their actual shown state. It implies that they are shown at
          // some point in the timeline.
          const shown = nodeState.shown || nodeState.shownSetByTween;

          // Once a trim state is set to false that's all we need to know
          if (trimObjectState[nodeId] !== false) {
            trimObjectState[nodeId] = !shown;
          }

        });

      });

    }

    const clippedObjects = trimConfig.graph.clipped 
      ? this.getClippedObjects() 
      : {};

    return {
      ...trimObjectState,
      ...clippedObjects
    };
  }

  // serialize the data in engine state for storage on BD server
  serialize(
    namespaceMap: {[key: string]: string}, 
    trimConfig: TrimConfiguration,
    replaceObjectId: boolean = false,
    sourceStory: StoryModel,
    sourceChapters: ChapterModel[],
    chapterIdMap
  ): {story: StoryModel} {
    
    // create a copy of the source story (the current story in state)
    const moduleData = cloneDeep(sourceStory);

    // replace story graph with serialized graph
    const trimMap = this.generateTrimObjectMap(trimConfig);
    moduleData.sceneGraph = serializeGraph(Human.scene.rootObjects, trimMap);

    // create BdGraph from trimmed sceneGraph for graph state calculations
    const graph = GraphUtils.createGraphFromSrc(
      moduleData.sceneGraph,
      'objectId', 
      'objects', 
      new GraphNodeState(Human.scene.colorRecipes.getRecipes())
    );

    const storyLabels = this.store.selectSnapshot(StoryLabelsState.getLabels);

    // replace storychapters with serialized chapters
    // I'm letting the chapter serializer keep renamespacing the min synchronization for now... it's too hairy to mess with right now.
    moduleData.chapters = sourceChapters.map(chapter =>
      this.chapterSerializer.serializeChapter(
        graph, 
        chapter, 
        namespaceMap, 
        storyLabels, 
        chapterIdMap
      )
    );

    // replace UI configuration for widget
    moduleData.ui = this.serializeUI(moduleData);

    // #NOTE: [EM] this is a hard overwrite, which will cause problems for BD content creators
    moduleData.animation = this.serializeAnimation(sourceChapters[0]) as StoryAnimationModel;
    
    moduleData.customData = {
      ...moduleData.customData,
      particleSystems: this.store.selectSnapshot(
        EngineParticlesState.getParticleSystemsList
      ),
    };

    // Quiz Settings: serialize with valid questions
    const quizQuestions = this.store.selectSnapshot(QuizState.getAllQuestions);
    const quizSettings = this.store.selectSnapshot(QuizState.getQuizSettings);
    if (quizSettings && quizQuestions.length > 0) {
      moduleData.quizSettings = quizSettings;
    } else {
      delete moduleData.quizSettings;
    }

    moduleData.tweenParticleLookup = this.store.selectSnapshot(EngineParticlesState.getTweenParticleLookup);

    // Tweens
    
    // Chapter timelineMaps only reference top level stateIds.
    // Therefore we can only trim the existing tweenLibraries
    const tweenStates = sourceChapters.reduce((accumulator, chapter) => {
      return accumulator.concat(
        this.chapterService.getChapterTweenStates(chapter)
      );
    }, []);

    moduleData.assetLibraries.tweens = 
      moduleData.assetLibraries.tweens.filter((tweenLibrary) => {
        const tweenState = this.getStateId(tweenLibrary);
        return tweenStates.indexOf(tweenState) >= 0;
      });

    // Lights
    const lightLibraries = sourceChapters.reduce((accumulator, chapter) => {
      return accumulator.concat(
        this.chapterService.getChapterLightLibraries(chapter)
      );
    }, []);

    moduleData.assetLibraries.lights = Array.from(new Set(lightLibraries));
    
    // Reflections 
    const globalReflectionLibraries = this.getReflectionLibraries(moduleData.reflections);

    const chapterReflectionLibraries = sourceChapters.reduce((accumulator, chapter) => {
      return accumulator.concat(
        this.chapterService.getChapterReflectionLibraries(chapter)
      );
    }, []);

    moduleData.assetLibraries.reflections = Array.from(
      new Set(globalReflectionLibraries.concat(chapterReflectionLibraries))
    );
    
    // Rebuild stateId array
    const assetLibraries = [].concat.apply(
      [], Object.values(moduleData.assetLibraries)
    );
    
    const sceneGraphStateIds = 
      this.getSceneGraphStateIds(moduleData.sceneGraph);

    moduleData.stateId = Array.from(
      new Set(assetLibraries.map(this.getStateId).concat(sceneGraphStateIds))
    );

    return {
      story: replaceObjectId 
        ? renamespace(moduleData, namespaceMap) 
        : moduleData
    };
  }

  private getReflectionLibraries(reflections: Object): string[] {
    reflections = reflections || {};
    
    const reflectionAssets = Object.values(reflections);

    // These are in the form <stateId>.<assetsubDirectory>.<assetId>
    return reflectionAssets.map((reflectionAsset) => {
      const assetParts = reflectionAsset.split('.');
      return [assetParts[0], assetParts[1]].join('/');
    });
  }

  private getSceneGraphStateIds(sceneGraph: any[]): string[] {
    return sceneGraph.map((rootObject) => {
      return rootObject.objectId.split('-')[0];
    });
  }

  private getStateId(assetId: string): string {
    return assetId.split('/')[0];
  }
}
